/**
 * The duel's screen furniture: both fighters' bars, the match clock, the
 * pre-duel freeze countdown, an escape menu with forfeit, and — the part that
 * has never existed — a card telling the player whether they won.
 *
 * ─── The result card is the point ───────────────────────────────────────────
 * Before this, a duel ended in silence. The arena modal hid itself when the
 * server pushed `LIVE`, the fight ran to completion in a room the client had
 * never joined, and the only thing the player ever saw was their HP bar
 * emptying and their carried bag gone. A win produced no signal at all.
 * `battleground:pvpConcluded` has been on the wire the whole time with nothing
 * listening to it.
 *
 * ─── Plain DOM, no panel registry ───────────────────────────────────────────
 * `ui/panels.ts` exists so the settlement's windows can answer "is anything
 * open" for the world's input gates. The arena is its own mounted view with no
 * other windows in it, so registering here would mean leaving a registration
 * behind for a torn-down element — the exact trap `engine/settings/` documents.
 */
import type { RoomPlayer, RoomStatus } from '../../state/battleground-state.types';
import { arenaArtUrl, embersHtml, preloadImages } from './embers';

export interface DuelHudOptions {
  selfName: string;
  opponentName: string;
  onForfeit: () => void;
  onLeave: () => void;
}

export interface DuelHudUpdate {
  freezeRemaining: number;
  timeRemaining: number;
  self: RoomPlayer | null;
  opponent: RoomPlayer | null;
  status: RoomStatus;
  /**
   * A wait with no countdown attached, or null.
   *
   * The arena view now mounts at LOBBY, so there is a stretch — the player's
   * map is up, the opponent's is not — with a live arena on screen, no room
   * joined and no number to show. Silence there reads as a duel that has
   * already started and is ignoring you, so the hold box says what is being
   * waited for. Superseded by the countdown the moment one exists.
   */
  waitingFor?: string | null;
}

export interface DuelHudHandles {
  update(next: DuelHudUpdate): void;
  showError(message: string): void;
  showResult(outcome: 'win' | 'lose' | 'draw'): void;
  toggleMenu(): void;
  dispose(): void;
}

/** `m:ss`. Mirrors `arena-state.ts`'s own `formatDuration` rather than importing it — same shape, different module boundary. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Whole seconds, rounded UP so an 8-second freeze reads 8…1 and never a stray 0. */
export function freezeSeconds(ms: number): number {
  return Math.max(0, Math.ceil(ms / 1000));
}

const VERDICT_COPY: Record<'win' | 'lose' | 'draw', { title: string; sub: string; kicker: string }> = {
  win: { title: 'Victory', sub: 'They went down first. You walk out.', kicker: 'Arena · last one standing' },
  lose: { title: 'Downed', sub: 'You wake at the Med-Center. Your gear is still yours.', kicker: 'Arena · you hit the dirt' },
  // A draw is `CANCELLED` server-side with no winner and no rating change —
  // worth saying plainly, because "nothing happened" is otherwise indis-
  // tinguishable from a bug.
  draw: { title: 'Draw', sub: 'Time ran out with both of you standing. Nothing changes hands.', kicker: 'Arena · the bell rang' },
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createDuelHud(container: HTMLElement, opts: DuelHudOptions): DuelHudHandles {
  const root = el('div', 'bv-duel');

  // ---- top bar ----
  const top = el('div', 'bv-duel-top');

  const buildFighter = (name: string, them: boolean) => {
    const wrap = el('div', them ? 'bv-duel-fighter is-them' : 'bv-duel-fighter');
    const label = el('div', 'bv-duel-name', name);
    const hpTrack = el('div', 'bv-duel-track');
    const hpFill = el('div', 'bv-duel-fill is-hp');
    hpTrack.appendChild(hpFill);
    const arTrack = el('div', 'bv-duel-track');
    const arFill = el('div', 'bv-duel-fill is-armor');
    arTrack.appendChild(arFill);
    wrap.append(label, hpTrack, arTrack);
    return {
      wrap, hpFill, arTrack, arFill,
    };
  };

  const self = buildFighter(opts.selfName, false);
  const them = buildFighter(opts.opponentName, true);
  const clock = el('div', 'bv-duel-clock', '—:—');
  top.append(self.wrap, clock, them.wrap);
  root.appendChild(top);

  // ---- freeze countdown ----
  const freeze = el('div', 'bv-duel-freeze');
  freeze.hidden = true;
  const freezeNum = el('span', undefined, '8');
  const freezeLabel = el('span', 'bv-duel-freeze-label', 'Hold');
  freeze.append(freezeNum, freezeLabel);
  root.appendChild(freeze);

  // ---- error ----
  const error = el('div', 'bv-duel-error');
  error.hidden = true;
  const errorText = el('p', undefined, '');
  const errorBtn = el('button', 'bv-duel-btn', 'Back to the settlement');
  errorBtn.type = 'button';
  error.append(errorText, errorBtn);
  root.appendChild(error);

  // ---- escape menu ----
  const menu = el('div', 'bv-duel-menu');
  menu.hidden = true;
  const menuCard = el('div', 'bv-duel-menu-card');
  const menuTitle = el('p', 'bv-duel-menu-title', 'Paused');
  const resumeBtn = el('button', 'bv-duel-btn', 'Back to the fight');
  resumeBtn.type = 'button';
  const forfeitBtn = el('button', 'bv-duel-btn is-danger', 'Forfeit the duel');
  forfeitBtn.type = 'button';
  menuCard.append(menuTitle, resumeBtn, forfeitBtn);
  menu.appendChild(menuCard);
  root.appendChild(menu);

  // ---- result ----
  // Janeks, 2026-10-16: *"after fight (win / loose popups) also more game
  // style ... make sure they have some action and animation"*. A full-screen
  // painted scene per outcome (Higgsfield, `public/ui/arena/`), drifting,
  // with embers and — on a win — turning light rays; the verdict stamps in
  // letter by letter and the card lands with a jolt. All CSS, keyed off the
  // `is-<outcome>` class on this root, so reduced-motion stops all of it.
  const result = el('div', 'bv-duel-result');
  result.hidden = true;
  const resultArt = el('div', 'bv-duel-result-art');
  resultArt.setAttribute('aria-hidden', 'true');
  const resultRays = el('div', 'bv-duel-result-rays');
  resultRays.setAttribute('aria-hidden', 'true');
  const resultCard = el('div', 'bv-duel-result-card');
  resultCard.setAttribute('role', 'dialog');
  resultCard.setAttribute('aria-modal', 'true');
  const band = el('span', 'bv-duel-result-band');
  band.setAttribute('aria-hidden', 'true');
  const kicker = el('p', 'bv-duel-kicker', '');
  const verdict = el('p', 'bv-duel-verdict', '');
  const rule = el('span', 'bv-duel-rule');
  rule.setAttribute('aria-hidden', 'true');
  rule.innerHTML = '<img src="/ui/arena/emblem.webp" alt="" width="44" height="44" decoding="async">';
  const verdictSub = el('p', 'bv-duel-sub', '');
  const resultBtn = el('button', 'bv-duel-btn', 'Back to the settlement');
  resultBtn.type = 'button';
  resultCard.append(band, kicker, verdict, rule, verdictSub, resultBtn);
  result.append(resultArt, resultRays);
  result.insertAdjacentHTML('beforeend', embersHtml(22));
  result.appendChild(resultCard);
  root.appendChild(result);

  // The result card shows at the decisive moment, and a display:none box's
  // background is never fetched — so fetch all three scenes now, while the
  // countdown is still running.
  preloadImages([arenaArtUrl('victory'), arenaArtUrl('defeat'), arenaArtUrl('draw'), '/ui/arena/emblem.webp']);

  container.appendChild(root);

  const leave = () => opts.onLeave();
  const forfeit = () => {
    // The room answers with a conclusion, which arrives as a result card — so
    // this closes the menu and waits rather than leaving immediately. Leaving
    // here would drop the connection and turn a clean forfeit into a
    // disconnect, which is the same outcome by a messier route.
    menu.hidden = true;
    opts.onForfeit();
  };
  const resume = () => { menu.hidden = true; };

  errorBtn.addEventListener('click', leave);
  resultBtn.addEventListener('click', leave);
  resumeBtn.addEventListener('click', resume);
  forfeitBtn.addEventListener('click', forfeit);

  const setBars = (
    rig: ReturnType<typeof buildFighter>,
    player: RoomPlayer | null,
  ): void => {
    const hp = player?.hp ?? 0;
    const maxHp = player?.maxHp && player.maxHp > 0 ? player.maxHp : Math.max(hp, 1);
    rig.hpFill.style.width = `${Math.min(100, Math.max(0, (hp / maxHp) * 100))}%`;

    const maxArmor = player?.maxArmor ?? 0;
    // No armour pool at all is a hidden bar, not an empty one: an empty track
    // reads as "your armour is gone", which is a different and alarming fact.
    rig.arTrack.style.display = maxArmor > 0 ? '' : 'none';
    if (maxArmor > 0) {
      const armor = player?.armor ?? 0;
      rig.arFill.style.width = `${Math.min(100, Math.max(0, (armor / maxArmor) * 100))}%`;
    }
  };

  let lastClock = '';
  let lastFreeze = -1;
  let lastWaiting: string | null = null;

  return {
    update(next: DuelHudUpdate): void {
      setBars(self, next.self);
      setBars(them, next.opponent);

      // The room has not answered yet — an em dash rather than a confident
      // "0:00", which would read as a duel that is already over.
      const clockText = next.status === 'joined' ? formatClock(next.timeRemaining) : '—:—';
      if (clockText !== lastClock) {
        lastClock = clockText;
        clock.textContent = clockText;
        clock.classList.toggle('is-low', next.status === 'joined' && next.timeRemaining <= 30_000);
      }

      const secs = next.freezeRemaining > 0 ? freezeSeconds(next.freezeRemaining) : 0;
      const waiting = secs > 0 ? null : (next.waitingFor ?? null);
      if (secs !== lastFreeze || waiting !== lastWaiting) {
        lastFreeze = secs;
        lastWaiting = waiting;
        freeze.hidden = secs <= 0 && !waiting;
        // The number and the label are one box in two moods: a count with
        // "Hold" under it, or a line of text on its own while the other
        // fighter loads.
        freezeNum.hidden = secs <= 0;
        if (secs > 0) freezeNum.textContent = String(secs);
        freezeLabel.textContent = secs > 0 ? 'Hold' : (waiting ?? 'Hold');
      }
    },

    showError(message: string): void {
      // Never overwrite a result with a connection error: the room closing is
      // the normal end of a duel, and the outcome is the more useful of the two
      // things to be looking at.
      if (!result.hidden) return;
      errorText.textContent = message;
      error.hidden = false;
      freeze.hidden = true;
    },

    showResult(outcome: 'win' | 'lose' | 'draw'): void {
      const copy = VERDICT_COPY[outcome];
      // One span per letter so the stamp can run down the word; textContent
      // is still exactly the title.
      verdict.innerHTML = [...copy.title]
        .map((ch, i) => `<span style="--i:${i}">${ch}</span>`)
        .join('');
      verdict.setAttribute('aria-label', copy.title);
      verdict.className = `bv-duel-verdict is-${outcome}`;
      kicker.textContent = copy.kicker;
      verdictSub.textContent = copy.sub;
      result.className = `bv-duel-result is-${outcome}`;
      error.hidden = true;
      menu.hidden = true;
      freeze.hidden = true;
      result.hidden = false;
    },

    toggleMenu(): void {
      // Not while a result is up — Escape there should not resurrect a paused
      // fight that has already been decided.
      if (!result.hidden) return;
      menu.hidden = !menu.hidden;
    },

    dispose(): void {
      errorBtn.removeEventListener('click', leave);
      resultBtn.removeEventListener('click', leave);
      resumeBtn.removeEventListener('click', resume);
      forfeitBtn.removeEventListener('click', forfeit);
      root.remove();
    },
  };
}
